// src/components/character_creation/SkillsSelection.jsx
// Выбор навыков (мастер создания).
// - ListItemText добавлен в импорт MUI (фикс ReferenceError).
// - Локальная копия localSkills синхронизируется с incoming selectedSkills.
// - Предупреждение о лимите показывается ТОЛЬКО при попытке выбрать 8-й навык,
//   а не постоянно при 7-и.
// - Всё совместимо с draft.skills: элементы могут быть объектами или строками (id).

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Grid,
  Alert,
  Paper,
  Stack,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,     // ← добавлено: фикс ReferenceError
  CircularProgress
} from '@mui/material';
import {
  Build as BuildIcon,
  Science as ScienceIcon,
  Groups as GroupsIcon,
  Info as InfoIcon,
  Lock as LockIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

import Gateway from '../../api/gateway';

const MAX_SKILLS = 7;

/** Нормализация одного элемента навыка: строка → объект {id, name}. */
const normalizeSkillRef = (ref, pool) => {
  if (!ref) return null;
  if (typeof ref === 'string') {
    const found = pool.find((s) => s.id === ref);
    return found || { id: ref, name: ref, description: '' };
  }
  if (typeof ref === 'object' && ref.id) return ref;
  return null;
};

/** Нормализация массива: строки или объекты → массив объектов. */
const normalizeSkillList = (list, pool) => {
  if (!Array.isArray(list)) return [];
  return list.map((r) => normalizeSkillRef(r, pool)).filter(Boolean);
};

const SkillsSelection = ({
  skills = [],
  race = null,
  characterTemplate = null,
  selectedSkills = [],
  onSkillsUpdate,
}) => {
  const [skillDetails, setSkillDetails] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [localSkillsList, setLocalSkillsList] = useState(
    Array.isArray(skills) ? skills : []
  );

  // 🔑 локальная копия выбранных, синхронизируется с props
  const [localSkills, setLocalSkills] = useState(() =>
    normalizeSkillList(selectedSkills, Array.isArray(skills) ? skills : [])
  );

  const safeSkills = Array.isArray(localSkillsList) ? localSkillsList : [];
  const currentSkillsCount = localSkills.length;
  const skillsRemaining = MAX_SKILLS - currentSkillsCount;
  const isAtLimit = currentSkillsCount >= MAX_SKILLS;

  // Обязательные навыки шаблона
  const requiredSkills = useMemo(() => {
    if (!characterTemplate?.primary_skills) return [];
    return Array.isArray(characterTemplate.primary_skills)
      ? characterTemplate.primary_skills
      : [];
  }, [characterTemplate]);

  // Заблокированные для расы
  const blockedSkills = useMemo(() => {
    if (!race?.incompatible_skills) return [];
    return Array.isArray(race.incompatible_skills) ? race.incompatible_skills : [];
  }, [race]);

  // Группировка по категориям
  const skillCategories = useMemo(() => {
    const categories = {};
    safeSkills.forEach((skill) => {
      const category = skill.category || 'other';
      if (!categories[category]) categories[category] = [];
      categories[category].push(skill);
    });
    return categories;
  }, [safeSkills]);

  // Синхронизация локального списка навыков с incoming
  useEffect(() => {
    if (Array.isArray(skills) && skills.length > 0) {
      setLocalSkillsList(skills);
      setLocalSkills((prev) => normalizeSkillList(prev, skills));
    } else if (!Array.isArray(skills) || skills.length === 0) {
      // если пусто — пробуем загрузить
      loadSkills();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skills]);

  // Синхронизация выбранных навыков с incoming selectedSkills
  useEffect(() => {
    const normalizedIncoming = normalizeSkillList(selectedSkills, safeSkills);
    // Сравниваем только по id, чтобы не пересоздавать state при каждом рендере
    const prevIds = localSkills.map((s) => s.id).join('|');
    const nextIds = normalizedIncoming.map((s) => s.id).join('|');
    if (prevIds !== nextIds) {
      setLocalSkills(normalizedIncoming);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSkills, safeSkills]);

  const getCategoryIcon = (category) => {
    const icons = {
      technical: <BuildIcon />,
      science: <ScienceIcon />,
      scientific: <ScienceIcon />,
      social: <GroupsIcon />,
      combat: '⚔️',
      exploration: '🧭',
      survival: '🛡️',
      production: '🏭',
      creative: '🎨',
      other: '🔧',
    };
    return icons[category] || '🔧';
  };

  const getCategoryName = (category) => {
    const names = {
      technical: 'Технические',
      science: 'Научные',
      scientific: 'Научные',
      social: 'Социальные',
      combat: 'Боевые',
      exploration: 'Исследовательские',
      survival: 'Навыки выживания',
      production: 'Производственные',
      creative: 'Творческие',
      other: 'Прочие',
    };
    return names[category] || category;
  };

  const isSkillSelected = (skillId) =>
    localSkills.some((skill) => skill.id === skillId);

  const isSkillRequired = (skillId) => requiredSkills.includes(skillId);

  const isSkillBlocked = (skillId) => blockedSkills.includes(skillId);

  const canSelectSkill = (skillId) => {
    if (isSkillBlocked(skillId)) return false;
    if (isSkillSelected(skillId)) return true;
    return !isAtLimit || isSkillRequired(skillId);
  };

  const handleSkillToggle = (skill) => {
    if (!skill) return;
    if (!canSelectSkill(skill.id)) {
      if (isAtLimit) {
        setError(
          `Достигнут лимит в ${MAX_SKILLS} навыков. Удалите один из выбранных навыков.`
        );
      }
      return;
    }

    let newSkills;
    if (isSkillSelected(skill.id)) {
      if (isSkillRequired(skill.id)) {
        setError('Этот навык обязателен и не может быть удален');
        return;
      }
      newSkills = localSkills.filter((s) => s.id !== skill.id);
    } else {
      if (isAtLimit && !isSkillRequired(skill.id)) {
        setError(
          `Достигнут лимит в ${MAX_SKILLS} навыков. Удалите один из выбранных навыков.`
        );
        return;
      }
      newSkills = [...localSkills, skill];
    }

    setLocalSkills(newSkills);
    if (onSkillsUpdate) onSkillsUpdate(newSkills);
    setError('');
  };

  const handleOpenDetails = (skill) => {
    setSkillDetails(skill);
    setDetailsOpen(true);
  };
  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSkillDetails(null);
  };

  const getSkillStatus = (skill) => {
    if (isSkillBlocked(skill.id)) {
      return { type: 'blocked', label: 'Недоступно для расы', color: 'error' };
    }
    if (isSkillRequired(skill.id)) {
      return { type: 'required', label: 'Обязательно для шаблона', color: 'success' };
    }
    if (isSkillSelected(skill.id)) {
      return { type: 'selected', label: 'Выбрано', color: 'primary' };
    }
    return { type: 'available', label: 'Доступно', color: 'default' };
  };

  const loadSkills = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await Gateway.api.ontology.get('skills');
      const list = Array.isArray(data) ? data : [];
      setLocalSkillsList(list);
      // перенормируем выбранные, чтобы объекты соответствовали новому пулу
      setLocalSkills((prev) => normalizeSkillList(prev, list));
    } catch (err) {
      console.error('❌ Ошибка загрузки навыков:', err);
      setError(`Не удалось загрузить навыки: ${Gateway.utils.getErrorMessage(err)}`);
    } finally {
      setLoading(false);
    }
  };

  // Автодобавление обязательных
  useEffect(() => {
    if (!characterTemplate || safeSkills.length === 0) return;
    const requiredObjs = safeSkills.filter(
      (skill) => requiredSkills.includes(skill.id) && !isSkillSelected(skill.id)
    );
    if (requiredObjs.length > 0) {
      const newSkills = [...localSkills, ...requiredObjs];
      setLocalSkills(newSkills);
      if (onSkillsUpdate) onSkillsUpdate(newSkills);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [characterTemplate, safeSkills, requiredSkills]);

  const handleClearSelection = () => {
    const requiredObjs = safeSkills.filter((skill) =>
      requiredSkills.includes(skill.id)
    );
    setLocalSkills(requiredObjs);
    if (onSkillsUpdate) onSkillsUpdate(requiredObjs);
    setError('');
  };

  const handleRecommendSkills = () => {
    if (!characterTemplate || safeSkills.length === 0) {
      setError('Для рекомендации нужен выбранный шаблон');
      return;
    }
    const recommendedIds = characterTemplate.primary_skills || [];
    const recommendedSkills = safeSkills
      .filter(
        (skill) =>
          recommendedIds.includes(skill.id) &&
          !isSkillBlocked(skill.id) &&
          !isSkillSelected(skill.id)
      )
      .slice(0, MAX_SKILLS - currentSkillsCount);
    if (recommendedSkills.length === 0) {
      setError('Нет рекомендуемых навыков для этого шаблона');
      return;
    }
    const newSkills = [...localSkills, ...recommendedSkills];
    setLocalSkills(newSkills);
    if (onSkillsUpdate) onSkillsUpdate(newSkills);
    setError('');
  };

  if (loading && safeSkills.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>Загрузка навыков...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Навыки и способности</Typography>
      <Typography variant="body1" paragraph color="text.secondary">
        Выберите до {MAX_SKILLS} навыков для вашего персонажа. Навыки определяют
        возможности и специализацию персонажа.
      </Typography>

      <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <Button
            variant="outlined"
            onClick={handleRecommendSkills}
            disabled={!characterTemplate || loading}
            startIcon={<CheckCircleIcon />}
            size="small"
          >
            Автовыбор по шаблону
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={handleClearSelection}
            disabled={localSkills.length === requiredSkills.length}
            startIcon={<RefreshIcon />}
            size="small"
          >
            Очистить выбор
          </Button>
          <Button
            variant="outlined"
            onClick={loadSkills}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
            size="small"
          >
            Обновить список
          </Button>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {(race || characterTemplate) && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'action.hover' }}>
          <Typography variant="subtitle2" gutterBottom color="text.secondary">
            Параметры фильтрации:
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            {race && (
              <Chip label={`Раса: ${race.name}`} size="small" variant="outlined" color="primary" />
            )}
            {characterTemplate && (
              <Chip label={`Шаблон: ${characterTemplate.name}`} size="small" variant="outlined" color="secondary" />
            )}
          </Stack>
        </Paper>
      )}

      {/* Прогресс */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1">
              Выбрано навыков: {currentSkillsCount} / {MAX_SKILLS}
            </Typography>
            <Chip
              label={`Осталось: ${skillsRemaining}`}
              color={skillsRemaining > 3 ? 'success' : skillsRemaining > 0 ? 'warning' : 'error'}
              variant="outlined"
            />
          </Box>
          <LinearProgress
            variant="determinate"
            value={(currentSkillsCount / MAX_SKILLS) * 100}
            color={currentSkillsCount === MAX_SKILLS ? 'error' : currentSkillsCount >= 5 ? 'warning' : 'success'}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </CardContent>
      </Card>

      {safeSkills.length === 0 ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Навыки не загружены. Попробуйте обновить список.
        </Alert>
      ) : (
        <>
          {requiredSkills.length > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Обязательные навыки для шаблона "{characterTemplate?.name}":
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {requiredSkills.map((skillId) => {
                  const skill = safeSkills.find((s) => s.id === skillId);
                  return skill ? (
                    <Chip
                      key={skillId}
                      label={skill.name}
                      color="success"
                      variant="filled"
                      size="small"
                      icon={<CheckCircleIcon />}
                      onClick={() => handleOpenDetails(skill)}
                      clickable
                    />
                  ) : null;
                })}
              </Stack>
            </Alert>
          )}

          {blockedSkills.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Недоступные навыки для расы "{race?.name}":
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {blockedSkills.map((skillId) => {
                  const skill = safeSkills.find((s) => s.id === skillId);
                  return skill ? (
                    <Chip
                      key={skillId}
                      label={skill.name}
                      color="error"
                      variant="outlined"
                      size="small"
                      icon={<LockIcon />}
                      onClick={() => handleOpenDetails(skill)}
                      clickable
                    />
                  ) : null;
                })}
              </Stack>
            </Alert>
          )}

          {currentSkillsCount > 0 && (
            <Card sx={{ mb: 3, border: '2px solid', borderColor: 'primary.main' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                  Выбранные навыки ({currentSkillsCount})
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {localSkills.map((skill) => (
                    <Tooltip key={skill.id} title={skill.description || ''}>
                      <Chip
                        label={skill.name}
                        color="primary"
                        variant="filled"
                        onDelete={() => handleSkillToggle(skill)}
                        deleteIcon={isSkillRequired(skill.id) ? <LockIcon /> : <CancelIcon />}
                        disabled={isSkillRequired(skill.id)}
                        onClick={() => handleOpenDetails(skill)}
                        clickable
                      />
                    </Tooltip>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          )}

          <Box>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              Доступные навыки по категориям
            </Typography>
            <Grid container spacing={2}>
              {Object.entries(skillCategories).map(([category, categorySkills]) => (
                <Grid item xs={12} md={6} key={category}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ mr: 1 }}>{getCategoryIcon(category)}</Box>
                        {getCategoryName(category)}
                        <Chip label={categorySkills.length} size="small" sx={{ ml: 1 }} />
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {categorySkills.map((skill) => {
                          const status = getSkillStatus(skill);
                          const canSelect = canSelectSkill(skill.id);
                          return (
                            <Tooltip
                              key={skill.id}
                              title={
                                <Box>
                                  <Typography variant="subtitle2">{skill.name}</Typography>
                                  <Typography variant="body2">{skill.description}</Typography>
                                  {status.type === 'blocked' && (
                                    <Typography variant="caption" color="error">
                                      Недоступно для вашей расы
                                    </Typography>
                                  )}
                                  {status.type === 'required' && (
                                    <Typography variant="caption" color="success">
                                      Обязательно для вашего шаблона
                                    </Typography>
                                  )}
                                </Box>
                              }
                            >
                              <Chip
                                label={skill.name}
                                variant={status.type === 'selected' ? 'filled' : 'outlined'}
                                color={status.color}
                                onClick={() => handleSkillToggle(skill)}
                                clickable={canSelect}
                                disabled={!canSelect && status.type !== 'selected' && status.type !== 'required'}
                                size="small"
                                icon={
                                  status.type === 'blocked' ? <LockIcon /> :
                                  status.type === 'required' ? <CheckCircleIcon /> :
                                  status.type === 'selected' ? <CheckCircleIcon /> :
                                  undefined
                                }
                                deleteIcon={<InfoIcon />}
                                onDelete={() => handleOpenDetails(skill)}
                              />
                            </Tooltip>
                          );
                        })}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </>
      )}

      {/* Диалог деталей */}
      <Dialog open={detailsOpen} onClose={handleCloseDetails} maxWidth="sm" fullWidth>
        {skillDetails && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {skillDetails.name}
                <Chip
                  label={getSkillStatus(skillDetails).label}
                  color={getSkillStatus(skillDetails).color}
                  size="small"
                  sx={{ ml: 2 }}
                />
              </Box>
            </DialogTitle>
            <DialogContent>
              <Stack spacing={2}>
                <Typography variant="body1">{skillDetails.description}</Typography>
                <Divider />
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Детали навыка:</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><BuildIcon /></ListItemIcon>
                      <ListItemText
                        primary="Категория"
                        secondary={getCategoryName(skillDetails.category || 'other')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ScienceIcon /></ListItemIcon>
                      <ListItemText
                        primary="Сложность"
                        secondary={skillDetails.difficulty || 'Средняя'}
                      />
                    </ListItem>
                    {skillDetails.requirements && (
                      <ListItem>
                        <ListItemIcon><WarningIcon /></ListItemIcon>
                        <ListItemText
                          primary="Требования"
                          secondary={skillDetails.requirements}
                        />
                      </ListItem>
                    )}
                  </List>
                </Box>
                {getSkillStatus(skillDetails).type === 'blocked' && (
                  <Alert severity="error">
                    Этот навык недоступен для выбранной расы "{race?.name}"
                  </Alert>
                )}
                {getSkillStatus(skillDetails).type === 'required' && (
                  <Alert severity="success">
                    Этот навык обязателен для выбранного шаблона "{characterTemplate?.name}"
                  </Alert>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDetails}>Закрыть</Button>
              {getSkillStatus(skillDetails).type !== 'blocked' && (
                <Button
                  variant="contained"
                  onClick={() => {
                    handleSkillToggle(skillDetails);
                    handleCloseDetails();
                  }}
                  disabled={!canSelectSkill(skillDetails.id)}
                  startIcon={isSkillSelected(skillDetails.id) ? <CancelIcon /> : <CheckCircleIcon />}
                >
                  {isSkillSelected(skillDetails.id) ? 'Отменить выбор' : 'Выбрать навык'}
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Как выбрать навыки:</strong>
          <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
            <li>Обязательные навыки выбираются автоматически</li>
            <li>Некоторые навыки могут быть недоступны для вашей расы</li>
            <li>Используйте автовыбор для получения рекомендаций по шаблону</li>
            <li>Нажмите на иконку информации ⓘ для просмотра деталей навыка</li>
          </ul>
        </Typography>
      </Alert>
    </Box>
  );
};

export default SkillsSelection;
